import { useEffect, useRef, useState, useCallback } from "react";

import { autoplayStore } from "../stores/preferences";

export const AUTOPLAY_COUNTDOWN_SECONDS = 10;

/**
 * useAutoplayCountdown — decides when the "próximo vídeo" pill shows and when
 * navigation to the next content fires.
 *
 * `timeupdate` only flips countdown visibility; the pill itself animates via
 * its own rAF loop (see AutoplayCountdown) so nothing re-renders per frame.
 */
export default function useAutoplayCountdown({ videoRef, contentId, nextContent, onNavigate }) {
    const [enabled, setEnabledState] = useState(autoplayStore.read);
    const [showCountdown, setShowCountdown] = useState(false);

    const cancelledRef = useRef(false);
    const triggeredRef = useRef(false);
    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;
    const showCountdownRef = useRef(false);
    showCountdownRef.current = showCountdown;
    const nextContentRef = useRef(nextContent);
    nextContentRef.current = nextContent;
    const onNavigateRef = useRef(onNavigate);
    onNavigateRef.current = onNavigate;

    const setEnabled = useCallback((value) => {
        autoplayStore.write(value);
    }, []);

    useEffect(() => autoplayStore.subscribe(setEnabledState), []);

    // Fresh video, fresh autoplay decision.
    useEffect(() => {
        cancelledRef.current = false;
        triggeredRef.current = false;
        setShowCountdown(false);
    }, [contentId]);

    const trigger = useCallback(() => {
        if (triggeredRef.current) return;
        if (!enabledRef.current || cancelledRef.current) return;
        if (!nextContentRef.current || !onNavigateRef.current) return;
        triggeredRef.current = true;
        setShowCountdown(false);
        onNavigateRef.current(nextContentRef.current);
    }, []);

    const cancel = useCallback(() => {
        cancelledRef.current = true;
        setShowCountdown(false);
    }, []);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handleTimeUpdate = () => {
            if (!video.duration) return;
            const remaining = video.duration - video.currentTime;
            const shouldShow =
                enabledRef.current
                && !cancelledRef.current
                && !!nextContentRef.current
                && remaining <= AUTOPLAY_COUNTDOWN_SECONDS
                && remaining > 0;

            if (shouldShow !== showCountdownRef.current) setShowCountdown(shouldShow);
        };

        video.addEventListener("timeupdate", handleTimeUpdate);
        return () => video.removeEventListener("timeupdate", handleTimeUpdate);
    }, [videoRef]);

    return { enabled, setEnabled, showCountdown, trigger, cancel };
}
